import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject, from, of } from 'rxjs';
import { concatMap, mergeMap, takeUntil, catchError } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { Document } from 'src/app/shared/document';
import { AgentDataApiService } from 'src/app/services/agent-data-api.service';
import {
  AgentRequestDetail,
  AgentCustomerLookup,
  AgentExtractedIntent,
  AgentResolvedLine,
  AgentDocumentSeed,
  AgentDocumentTypeGuess,
  AgentProductCandidate,
  AgentCustomerResolution,
  AgentResolutionJson,
} from 'src/app/shared/agent-data';
import { AgentQuickCreateResult } from 'src/app/visualcomponents/agent-customer-quick-create/agent-customer-quick-create.component';

/**
 * "AI Assist" tab content for `create-new-document`'s confirmation panel v3 -- see
 * `docs/PROMPT_agent-intake-confirmation-panel.md`.
 *
 * Design v3 (owner review 2026-08-03): this is the *same* panel as the standard "Новый
 * документ" flow -- doc-type button row first, then one `p-fieldset > p-accordion` holding
 * the same four blocks in the same order (dates/currency/our company -> counterparty search
 * -> counterparty result -> delivery conditions), rendered by the very same child components
 * the host dialog uses. The AI's own findings are interleaved *between* those blocks rather
 * than stacked above them: the source email opens the accordion, the customer findings sit
 * right against the counterparty confirmation they inform, and the extracted products sit
 * last, where the real product panel will land next cycle.
 *
 * Because a `p-accordion` only picks up `p-accordionTab`s declared in its own template,
 * interleaving means this component renders the standard blocks itself and the host
 * suppresses its own copy while this tab is active (`isAgentTabActive`). The blocks are
 * thin wrappers around the identical shared components with identical bindings -- if you
 * change one side, change the other: `create-new-document.component.html`, the accordion
 * under "Document Creation Form".
 *
 * Deliberately does NOT create a document itself (the host dialog's existing
 * `onCreateDocument()`/`doSaveDocument()` does that, unchanged) and does NOT write
 * `AgentRequestLines` (see the design doc's "What this does NOT do" -- a write-once
 * snapshot would go stale the moment the resulting document is edited afterward).
 */
@Component({
  selector: 'app-new-document-dlg-ai-assist',
  templateUrl: './new-document-dlg-ai-assist.component.html',
  styleUrls: ['./new-document-dlg-ai-assist.component.css'],
})
export class NewDocumentDlgAiAssistComponent implements OnChanges, OnDestroy {

  @Input() requestId: number | null = null;

  /** The host's `newDocument`, once a doc-type button has seeded it -- passed straight
   *  through to the standard block components below, exactly as the host passes it to its
   *  own copies of them. Null until then, which is what hides those blocks. */
  @Input() newDocument: Document | null = null;

  /** Always null in the agent flow (a claimed email has no parent document), but the
   *  standard block components take it, so it is forwarded rather than hard-coded. */
  @Input() parentDocument: Document | null = null;

  /** `activeConfig.header` from the host -- "Счет" / "Коммерческое предложение", the same
   *  fieldset caption the standard flow shows. */
  @Input() docHeader: string | null = null;

  /** Host getters driving the standard blocks' initial open/closed state and header
   *  suffix; forwarded so those blocks behave identically to the host's own copies. */
  @Input() resultPartnerName = '';
  @Input() checkSelectedClientData = false;
  @Input() needClentSearch = true;

  /** Fired when a doc-type button is clicked -- the host seeds `newDocument` from this,
   *  the same way tab 1's own buttons seed it. */
  @Output() applyToDocument = new EventEmitter<AgentDocumentSeed>();

  /** Fired when the manager accepts/skips a catalog candidate *after* the document was
   *  already seeded. Narrower than `applyToDocument` on purpose: re-emitting the full seed
   *  would rebuild `newDocument` from scratch and silently discard a counterparty the
   *  manager had just corrected by hand in the blocks below. */
  @Output() itemsChange = new EventEmitter<AgentDocumentSeed['items']>();

  /** Forwarded from the embedded `app-company-search` to the host's own `onSelectedIds()`
   *  -- the free-form search -> counterparty selector handoff, unchanged. */
  @Output() selectedIds = new EventEmitter<Record<string, number>>();

  /** Forwarded from the embedded standard blocks to the host's `onNewDocumentUpdate()`. */
  @Output() newDocumentUpdate = new EventEmitter<Document>();

  /** Fired after `PATCH .../requests/:id` with `outcome: 'cancelled'` succeeds -- the
   *  host dialog closes itself, no document was ever created. */
  @Output() abandoned = new EventEmitter<void>();

  private destroy$ = new Subject<void>();

  loading = false;
  loadError: string | null = null;

  request: AgentRequestDetail | null = null;
  intent: AgentExtractedIntent | null = null;
  customer: AgentCustomerLookup | null = null;

  /** How the agent resolved the sender, straight from `resolution_json`. Shown verbatim:
   *  when the panel says no customer was found, this is the only thing that says whether
   *  the sender is genuinely unknown or the resolver lost a contact that does exist -- a
   *  distinction the manager can act on (correct it by hand below and report the miss). */
  customerResolution: AgentCustomerResolution | null = null;

  resolvedLines: AgentResolvedLine[] = [];

  /** How many of `resolvedLines` have finished their catalog search -- drives the
   *  "Товары из письма" section header's progress text. `resolveAllLines()` runs
   *  sequentially (`concatMap`), so without this a line waiting its turn showed
   *  `loading: false` + zero candidates, which rendered identically to a genuine
   *  "no catalog matches" result -- the manager had no way to tell "not found yet"
   *  from "found nothing", which is what made the panel look like it stopped after
   *  the first item. */
  resolvedLinesDone = 0;

  /** What the LLM guessed from the email. Applied automatically once the request (and its
   *  customer, which decides key-account/COPILOT mode) has loaded, so the panel opens with
   *  every block already populated instead of an empty shell -- then behaves exactly like
   *  the standard tab: clicking the other button re-seeds the document. */
  suggestedDocType: AgentDocumentTypeGuess = 'proforma';

  /** DOCTYPE currently seeded into the host's `newDocument` (12 = КП, 1 = счет), or null
   *  before the first seed. Drives the button highlight, same as the host's own `action`. */
  appliedDocType: 1 | 12 | null = null;

  abandonReason = '';
  showAbandonInput = false;
  abandoning = false;

  constructor(
    private agentApi: AgentDataApiService,
    private messageService: MessageService,
  ) { }

  /** Whether the sequential catalog search (`resolveAllLines`) is still running --
   *  drives the "Товары из письма" section header's progress indicator. Search runs
   *  in the background and never blocks the rest of the panel (doc-type/counterparty/
   *  delivery blocks above are all independently interactive throughout); this only
   *  exists so the manager can tell "still searching" from "done, nothing found". */
  get resolvingProducts(): boolean {
    return this.resolvedLines.length > 0 && this.resolvedLinesDone < this.resolvedLines.length;
  }

  /** COPILOT mode -- `key_partner` and `key_account` both qualify (2026-09-15):
   *  the two "our most important customers" tiers, one because the discount is
   *  always maximal, the other because handling is case-by-case with a
   *  dedicated manager. Name kept as `isKeyAccount` even though it now covers
   *  two tier values -- renaming would touch the template and every call site
   *  for no behavioural gain. */
  get isKeyAccount(): boolean {
    const tier = this.customer?.tier?.tier;
    return tier === 'key_account' || tier === 'key_partner';
  }

  /**
   * Whether the counterparty block opens expanded. The host's own `checkSelectedClientData`
   * opens it when any of the four buyer fields is still empty, which is the right rule for
   * a manually-started document. Here it is not sufficient: the agent now pre-fills all
   * four from an exact-address match, so that flag goes false and the block would collapse
   * with the manager never having seen what was assumed on their behalf. The panel's own
   * rule (CLAUDE.md, "Agent intake panel — build rules") is that an agent's pre-selection
   * is pre-*selected*, never silently trusted -- so a prefilled counterparty opens too.
   */
  get openCounterpartyBlock(): boolean {
    return this.checkSelectedClientData || !!this.request?.customer_id;
  }

  /**
   * Free-form counterparty search opens only while there is nobody to confirm.
   * The host's own `needClentSearch` answers a different question -- "is a search
   * relevant at all", i.e. is this document without a parent to inherit a
   * counterparty from -- which on the agent path is always true, so the block
   * stayed open even after the agent had resolved the customer and filled the
   * result block below it. Gated on the buyer actually set on the document rather
   * than on `request.customer_id`, so it reflects what «Результат поиска
   * контрагента» currently shows: clear the buyer there and the search reopens.
   */
  get openClientSearchBlock(): boolean {
    return this.needClentSearch && !this.newDocument?.DOCCLIENTSID;
  }

  /**
   * AG-21b -- whether to offer the quick-create widget, and which mode.
   * `'new-company'` when no company is selected in the document yet (the
   * common LOW-confidence case, nobody matched at all); `'new-contact'`
   * once a company *is* selected -- which can happen via Step 1b's domain
   * match, a Step 3 name/reg-code match, or a manual search -- but the
   * extracted signals still name a person that isn't a known contact
   * there. Gated on the same "is there anything useful to act on" check
   * the design doc specifies: a LOW-confidence result with every extracted
   * field null (Step 2 ran but found nothing) offers nothing.
   */
  get quickCreateMode(): 'new-company' | 'new-contact' | null {
    const s = this.customerResolution?.extractedSignals;
    if (!s || !(s.companyName || s.contactFullName)) { return null; }
    return this.newDocument?.DOCCLIENTSID ? 'new-contact' : 'new-company';
  }

  /**
   * AG-22 -- whether to offer the customer/contact update-from-extracted-signals widget.
   * Only shown when a customer was already resolved (customer_id is set) *and* extracted
   * signals exist (Step 3 match path), meaning there is something to diff against.
   * The component itself renders nothing if the diff is empty (no actual differences found).
   */
  get showUpdateOffer(): boolean {
    return !!this.request?.customer_id && !!this.customerResolution?.extractedSignals;
  }

  /**
   * `request.raw_email_text` is the raw MIME body as received -- often full
   * HTML (`<html><head>...`), sometimes plain text, depending on how the
   * sender's client composed it. Showing that HTML source verbatim in a
   * `<textarea>` renders as unreadable markup, not the message. `DOMParser`
   * strips tags/scripts/styles down to just the rendered text (and decodes
   * entities like `&nbsp;` along the way) without ever inserting the
   * untrusted content into the live page DOM -- no `innerHTML`, nothing
   * executes, safe for an external sender's email body. Falls back to the
   * raw string as-is for genuinely plain-text emails (parsing a string with
   * no tags just returns it unchanged).
   */
  get readableEmailText(): string {
    const raw = this.request?.raw_email_text;
    if (!raw) { return ''; }
    const doc = new DOMParser().parseFromString(raw, 'text/html');
    return (doc.body.textContent || '').trim() || raw;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['requestId'] && this.requestId) {
      this.loadRequest(this.requestId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadRequest(id: number): void {
    this.loading = true;
    this.loadError = null;
    this.request = null;
    this.intent = null;
    this.customer = null;
    this.customerResolution = null;
    this.resolvedLines = [];
    this.appliedDocType = null;
    this.showAbandonInput = false;
    this.abandonReason = '';

    this.agentApi.getRequest(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ request }) => {
        this.request = request;
        this.intent = this.parseExtractedJson(request.extracted_json);
        this.customerResolution = this.parseResolutionJson(request.resolution_json);
        this.suggestedDocType = (this.intent?.documentType && this.intent.documentType !== 'unknown')
          ? this.intent.documentType : 'proforma';
        // `loading: true` from the start -- these are queued for a sequential search
        // (see resolveAllLines), not yet known to have zero matches. Rendering the
        // real "still searching" state for every line, not just the one currently in
        // flight, is what resolvedLinesDone's progress text below depends on.
        this.resolvedLines = (this.intent?.items ?? []).map(item => ({
          extracted: item, candidates: [], loading: true, accepted: null,
        }));
        this.resolvedLinesDone = 0;
        this.loading = false;

        this.resolveAllLines();

        // The auto-seed waits for the customer lookup: `isKeyAccount` comes from it, and a
        // key account must NOT get a document seeded (COPILOT mode -- the manager handles it
        // by hand, see the design doc). Seeding first and discovering the tier afterwards
        // would leave a half-created document on screen for exactly the accounts where that
        // is least acceptable.
        if (request.customer_id) {
          this.agentApi.getCustomer(request.customer_id).pipe(takeUntil(this.destroy$)).subscribe({
            next: customer => {
              this.customer = customer;
              this.autoApplySuggestedDocType();
            },
            error: err => {
              console.error('*** NewDocumentDlgAiAssistComponent getCustomer error:', err);
              // Tier unknown -- treat as a normal customer rather than blocking the panel.
              this.autoApplySuggestedDocType();
            },
          });
        } else {
          this.autoApplySuggestedDocType();
        }
      },
      error: err => {
        console.error('*** NewDocumentDlgAiAssistComponent getRequest error:', err);
        this.loadError = 'Не удалось загрузить заявку.';
        this.loading = false;
      },
    });
  }

  private parseExtractedJson(raw: string | null): AgentExtractedIntent | null {
    if (!raw) { return null; }
    try {
      return JSON.parse(raw) as AgentExtractedIntent;
    } catch (err) {
      console.error('*** NewDocumentDlgAiAssistComponent: extracted_json parse failed:', err);
      return null;
    }
  }

  /** `resolution_json` also carries unrelated keys merged in by the Teams Ignore flow, so a
   *  missing `customerResolution` is normal, not an error. */
  private parseResolutionJson(raw: string | null): AgentCustomerResolution | null {
    if (!raw) { return null; }
    try {
      return (JSON.parse(raw) as AgentResolutionJson).customerResolution ?? null;
    } catch (err) {
      console.error('*** NewDocumentDlgAiAssistComponent: resolution_json parse failed:', err);
      return null;
    }
  }

  /** How many lines' catalog searches run at once. Bounded, not unlimited (`forkJoin` or a
   *  bare `mergeMap`) -- a real request line can fire two round trips (part-number search,
   *  then a description fallback if that comes back empty), so an email with several items
   *  could otherwise burst well past a handful of simultaneous hits to a fuzzy DB search
   *  route. 4 keeps that burst small while still resolving items in parallel instead of one
   *  at a time. */
  private static readonly PRODUCT_RESOLVE_CONCURRENCY = 4;

  /** Resolves every extracted line's catalog candidates in parallel, capped at
   *  `PRODUCT_RESOLVE_CONCURRENCY` in flight at once -- this used to be a strictly
   *  sequential `concatMap` pass, which meant total wait time grew with the number of items
   *  in the email; a bounded `mergeMap` gets the same "don't flood the backend" property
   *  while resolving several lines at once, so the whole search finishes in roughly one
   *  round trip's worth of time instead of N.
   *
   *  Tries `partNumber` first when the extractor found one, falling back to `description`
   *  only if that search comes back empty. `POST /resolve` is a fuzzy AND-across-words
   *  match against the catalog (`WY_tvf_searchProductsSimple`, shared with `GET
   *  /Product/SimpleSearch`) -- a real Ukrainian request line like "Модуль автоматики
   *  ADAM-6017" needs every one of those words to appear in the catalog text to match, and
   *  the catalog's actual name ("Модуль ізольованого аналогового введення...") shares none
   *  of them. The part number alone is exactly the code-like signal
   *  `webyto-agent`'s `intent-extractor.ts` extracts separately for this reason (see its own
   *  `ExtractedItem.partNumber` doc comment) -- querying with the free-text description
   *  first, or only, throws that signal away and returns "no matches" for a part that's
   *  actually in the catalog. */
  private resolveAllLines(): void {
    from(this.resolvedLines).pipe(
      mergeMap(line => {
        const partNumber = line.extracted.partNumber;
        const primaryQuery = partNumber || line.extracted.description;
        return this.agentApi.resolveProduct(primaryQuery).pipe(
          concatMap(res => (res.candidates.length === 0 && partNumber)
            ? this.agentApi.resolveProduct(line.extracted.description)
            : of(res)),
          catchError(err => {
            console.error('*** NewDocumentDlgAiAssistComponent resolveProduct error:', err);
            return of({ candidates: [] as AgentProductCandidate[] });
          }),
          concatMap(res => {
            line.candidates = res.candidates;
            line.loading = false;
            this.resolvedLinesDone++;
            return of(line);
          }),
        );
      }, NewDocumentDlgAiAssistComponent.PRODUCT_RESOLVE_CONCURRENCY),
      takeUntil(this.destroy$),
    ).subscribe();
  }

  acceptCandidate(line: AgentResolvedLine, candidate: AgentProductCandidate): void {
    line.accepted = candidate;
    this.emitItems();
  }

  skipCandidate(line: AgentResolvedLine): void {
    line.accepted = null;
    this.emitItems();
  }

  /** Current accepted candidates in `AgentDocumentSeed` item shape. */
  private acceptedItems(): AgentDocumentSeed['items'] {
    return this.resolvedLines
      .filter((line): line is AgentResolvedLine & { accepted: AgentProductCandidate } => !!line.accepted)
      .map(line => ({
        prodId: line.accepted.PRODID,
        prodCode: line.accepted.PRODCODE,
        prodName: line.accepted.PRODNAME,
        qty: line.extracted.qty || 1,
      }));
  }

  /** Pushes the accepted products into an already-seeded document without re-seeding it --
   *  see `itemsChange`'s own comment for why this is not just another `applyToDocument`.
   *
   *  Checks `this.newDocument` (the host's real state), not `this.appliedDocType` (whether
   *  *this component's own* `applyDocType()` was the one that seeded it) -- 2026-09-15,
   *  fixed alongside un-hiding the candidates panel for `isKeyAccount`. A key account's
   *  document is seeded from the standard Proforma/Invoice tab instead (`applyDocType()`
   *  stays blocked here, COPILOT mode's whole point), so `appliedDocType` never gets set
   *  in this component even once a real document exists -- accepting a candidate would
   *  have silently done nothing. `newDocument` reflects the host's actual state regardless
   *  of which tab created it, so this covers both paths with one check. */
  private emitItems(): void {
    if (!this.newDocument) { return; }
    this.itemsChange.emit(this.acceptedItems());
  }

  /** Seeds the document with whatever the LLM guessed, once, on load. */
  private autoApplySuggestedDocType(): void {
    if (this.appliedDocType || this.isKeyAccount) { return; }
    this.applyDocType(this.suggestedDocType === 'invoice' ? 1 : 12);
  }

  /**
   * Fired by one of the two doc-type buttons in the first row -- the same
   * click-immediately-seeds-`newDocument` behaviour as the standard tab's own
   * `onCreateProforma`/`onCreateInvoice` buttons, including the part where clicking the
   * other type afterwards rebuilds the document. Products accepted so far ride along in the
   * seed, so switching КП <-> счет keeps them.
   */
  applyDocType(docType: 1 | 12): void {
    if (!this.request || this.isKeyAccount) { return; }

    this.applyToDocument.emit({
      requestId: this.request.id,
      docType,
      customerId: this.request.customer_id,
      // Only Step 1's exact-address match knows this; null from every other path, and the
      // host leaves the contact to its usual default in that case rather than guessing.
      personId: this.customerResolution?.personId ?? null,
      companyId: this.request.resolved_company_id,
      items: this.acceptedItems(),
    });
    this.appliedDocType = docType;
  }

  /**
   * AG-21b -- the quick-create widget never writes into `newDocument`
   * itself (same "never silently trusted at default" rule every other
   * resolution path here follows). This mirrors
   * `NewDocumentDlgCompaniesComponent.cliCompanySelected()`'s own mutate-
   * then-emit pattern exactly -- `newDocument` is the same object reference
   * passed to both that component and this one, so mutating it here and
   * re-emitting `newDocumentUpdate` (the existing, already-wired output) is
   * the established refresh path, not a new one.
   */
  onQuickCreateResult(result: AgentQuickCreateResult): void {
    if (!this.newDocument) { return; }
    if (result.cliId) {
      this.newDocument.DOCCLIENTSID = result.cliId;
      this.newDocument.DOCPCOCLIID = result.cliId;
    }
    if (result.persId) {
      this.newDocument.DOCPERSONID = result.persId;
    }
    this.newDocumentUpdate.emit(this.newDocument);
  }

  onAbandon(): void {
    if (!this.request || !this.abandonReason.trim()) { return; }

    this.abandoning = true;
    this.agentApi.confirmRequest(this.request.id, 'cancelled', { rejectionReason: this.abandonReason.trim() })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.abandoning = false;
          this.messageService.add({ severity: 'info', summary: 'Заявка отклонена', detail: this.abandonReason });
          this.abandoned.emit();
        },
        error: err => {
          this.abandoning = false;
          console.error('*** NewDocumentDlgAiAssistComponent confirmRequest(cancelled) error:', err);
          this.messageService.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось отклонить заявку.' });
        },
      });
  }
}
